import type { Id, IsoDate, YearMonth } from '../data/types';

/**
 * Parsing difensivo dei filtri globali dall'URL.
 *
 * L'URL arriva da link condivisi, segnalibri vecchi o modifiche a mano: ogni valore va
 * validato contro formato e limiti del dataset corrente. Un valore non valido torna al
 * default invece di raggiungere i motori analytics, che lanciano su periodi invertiti.
 */

export interface UrlBounds {
  minMonth: YearMonth;
  maxMonth: YearMonth;
  minDate: IsoDate;
  maxDate: IsoDate;
}

export interface KnownIds {
  accountIds: ReadonlySet<Id>;
  categoryIds: ReadonlySet<Id>;
}

export interface GlobalFilterState {
  fromMonth: YearMonth;
  toMonth: YearMonth;
  fromDate: IsoDate;
  toDate: IsoDate;
  accountFilterActive: boolean;
  accountIds: Id[];
  categoryFilterActive: boolean;
  categoryIds: Id[];
  includeUncategorized: boolean;
}

const MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;
const DATE_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function isYearMonth(value: string | null | undefined): value is YearMonth {
  return typeof value === 'string' && MONTH_PATTERN.test(value);
}

export function isIsoDate(value: string | null | undefined): value is IsoDate {
  const match = typeof value === 'string' ? DATE_PATTERN.exec(value) : null;
  if (!match) return false;
  const [, year, month, day] = match.map(Number) as [number, number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function clamp<T extends string>(value: T, min: T, max: T): T {
  return value < min ? min : value > max ? max : value;
}

/**
 * Restituisce un intervallo valido e ordinato dentro `[min, max]`.
 * Valori malformati tornano al limite corrispondente; un intervallo invertito viene scambiato.
 */
export function parseRange<T extends string>(
  rawFrom: string | null,
  rawTo: string | null,
  min: T,
  max: T,
  isValid: (value: string | null) => value is T,
): { from: T; to: T } {
  const from = clamp(isValid(rawFrom) ? rawFrom : min, min, max);
  const to = clamp(isValid(rawTo) ? rawTo : max, min, max);
  return from <= to ? { from, to } : { from: to, to: from };
}

function parseIdList(raw: string | null, known: ReadonlySet<Id>): Id[] {
  if (raw === null) return [];
  return Array.from(new Set(raw.split(',').filter((id) => id && known.has(id))));
}

export function parseGlobalFilters(
  params: URLSearchParams,
  bounds: UrlBounds,
  known: KnownIds,
  supportsCategories: boolean,
): GlobalFilterState {
  const months = parseRange(
    params.get('fromMonth'),
    params.get('toMonth'),
    bounds.minMonth,
    bounds.maxMonth,
    isYearMonth,
  );
  const dates = parseRange(
    params.get('from'),
    params.get('to'),
    bounds.minDate,
    bounds.maxDate,
    isIsoDate,
  );
  const categoryFilterActive = supportsCategories && params.has('categories');
  return {
    fromMonth: months.from,
    toMonth: months.to,
    fromDate: dates.from,
    toDate: dates.to,
    accountFilterActive: params.has('accounts'),
    accountIds: parseIdList(params.get('accounts'), known.accountIds),
    categoryFilterActive,
    categoryIds: categoryFilterActive
      ? parseIdList(params.get('categories'), known.categoryIds)
      : [],
    includeUncategorized: categoryFilterActive && params.get('uncategorized') === '1',
  };
}
